import {
  provideHttpClient,
  withFetch,
  withInterceptors,
} from '@angular/common/http';
import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  isDevMode,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getAuth, provideAuth } from '@angular/fire/auth';
import { getFirestore, provideFirestore } from '@angular/fire/firestore';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter } from '@angular/router';
import {
  BulkUpdateActionEffects,
  CDW_BULK_UPDATE_ACTION_FEATURE_KEY,
  cdwBulkUpdateActionReducer,
} from '@cdw-ae/common-ui-components-bulk-update-action';
import {
  CDW_DRAWERS_FEATURE_KEY,
  cdwDrawersReducer,
} from '@cdw-ae/common-ui-components-drawer';
import {
  CUSTOMER_LOOKUP_STRATEGY,
  CUSTOMER_SETTINGS_FEATURE_KEY,
  CustomerLookupModule,
  CustomerSettingsEffects,
  customerSettingsReducer,
  MockCustomerLookupStrategy,
} from '@cdw-ae/customer-lookup';
import { OU_TREE_DATA_PROVIDER } from '@cdw-ae/filter-bar-selection-matchers';
import {
  JOBS_STATUS_FEATURE_KEY,
  jobsStatusReducer,
} from '@cdw-ae/jobs-status-state';
import { API_URL_BUILDER } from '@cdw-ae/routes-shared-api';
import {
  FilterBarParamStrategy,
  UrlStateSerializerService,
} from '@cdw-ae/url-params';
import { provideEffects } from '@ngrx/effects';
import { provideRouterStore, routerReducer } from '@ngrx/router-store';
import { provideState, provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { environment } from '../environments/environment';
import { AccessManagementEffects } from './+state/access-management/access-management.effects';
import * as fromAccessManagement from './+state/access-management/access-management.reducer';
import { AppLifecyclesEffects } from './+state/app-lifecycles/app-lifecycles.effects';
import * as fromAppLifecycles from './+state/app-lifecycles/app-lifecycles.reducer';
import { SessionTimeoutEffects } from './+state/app-lifecycles/session-timeout.effects';
import { AppSettingsEffects } from './+state/app-settings.effects';
import * as fromAppSettings from './+state/app-settings.reducer';
import { DrivePickerEffects } from './+state/drive-picker/drive-picker.effects';
import {
  DRIVE_PICKER_FEATURE_KEY,
  drivePickerReducer,
} from './+state/drive-picker/drive-picker.reducer';
import { FilterEffects } from './+state/filter/filter.effects';
import {
  APP_FILTERS_FEATURE_KEY,
  filterReducer,
} from './+state/filter/filter.reducer';
import { JobsStatusEffects } from './+state/jobs-status/jobs-status.effects';
import { UserContextSettingsEffects } from './+state/user-context/user-context-settings.effects';
import * as fromUserContextSettings from './+state/user-context/user-context-settings.reducer';
import { UserIdentitySettingsEffects } from './+state/user-identity/user-identity-settings.effects';
import * as fromUserIdentitySettings from './+state/user-identity/user-identity-settings.reducer';
import { appRoutes } from './app.routes';
import {
  ACCESS_MANAGEMENT_FRONTEND_AUTH_SERVICE,
  SESSION_LOGOUT_HANDLER,
} from './app.tokens';
import { firebaseAuthInterceptor } from './common/interceptors/firebase-auth-interceptor';
import { GoogleTagManagerService } from './common/services/analytics/google-tag-manager.service';
import { ApiUrlBuilderService } from './common/services/api-url-builder.service';
import { AuthenticationService } from './common/services/authentication/authentication.service';
import { UserIdentityService } from './common/services/userIdentity/user-identity.service';
import { AccessManagementComponent } from './layouts/internal/components/administration/access-management/access-management.component';
import { AccessManagementFrontendAuthService } from './layouts/internal/components/administration/access-management/access-management-frontend-auth.service';
import { OrgUnitsDataService } from './layouts/internal/components/administration/access-management/org-units/org-units-data.service';

export function initializeAuthFactory() {
  const userIdentityService = inject(UserIdentityService);
  return userIdentityService.initializeAuthState();
}

export function initializeGtmFactory() {
  const gtmService = inject(GoogleTagManagerService);
  gtmService.init();
}

export const appConfig: ApplicationConfig = {
  providers: [
    // Core NgRx Store Setup
    provideStore({ router: routerReducer }),
    provideStoreDevtools({ logOnly: !isDevMode() }),
    provideRouterStore(),

    // Feature State Registrations (all states before effects)
    provideState(CDW_DRAWERS_FEATURE_KEY, cdwDrawersReducer),
    provideState(
      CDW_BULK_UPDATE_ACTION_FEATURE_KEY,
      cdwBulkUpdateActionReducer
    ),
    provideState(
      fromAppLifecycles.APP_LIFECYCLES_FEATURE_KEY,
      fromAppLifecycles.appLifecyclesReducer
    ),
    provideState(
      fromUserIdentitySettings.USER_IDENTITY_SETTINGS_FEATURE_KEY,
      fromUserIdentitySettings.userIdentitySettingsReducer
    ),
    provideState(
      fromUserContextSettings.USER_CONTEXT_SETTINGS_FEATURE_KEY,
      fromUserContextSettings.userContextSettingsReducer
    ),
    provideState(CUSTOMER_SETTINGS_FEATURE_KEY, customerSettingsReducer),
    provideState(
      fromAppSettings.APP_SETTINGS_FEATURE_KEY,
      fromAppSettings.appSettingsReducer
    ),
    provideState(APP_FILTERS_FEATURE_KEY, filterReducer),
    provideState(
      fromAccessManagement.ACCESS_MANAGEMENT_FEATURE_KEY,
      fromAccessManagement.accessManagementReducer
    ),
    provideState(JOBS_STATUS_FEATURE_KEY, jobsStatusReducer),
    provideState(DRIVE_PICKER_FEATURE_KEY, drivePickerReducer),

    // Service Providers (must be before effects that depend on them)
    AccessManagementComponent,
    UrlStateSerializerService,
    FilterBarParamStrategy,
    {
      provide: ACCESS_MANAGEMENT_FRONTEND_AUTH_SERVICE,
      useClass: AccessManagementFrontendAuthService,
    },
    // EDUS-2657: the session timeout terminates through the normal logout
    // path, but reaches it via a token so it does not import Firebase.
    {
      provide: SESSION_LOGOUT_HANDLER,
      useExisting: AuthenticationService,
    },
    provideAppInitializer(initializeGtmFactory),

    // Feature Effects Registrations (after all states and dependencies are registered)
    provideEffects([
      AppLifecyclesEffects,
      SessionTimeoutEffects,
      UserIdentitySettingsEffects,
      UserContextSettingsEffects,
      CustomerSettingsEffects,
      AppSettingsEffects,
      FilterEffects,
      AccessManagementEffects,
      BulkUpdateActionEffects,
      JobsStatusEffects,
      DrivePickerEffects,
    ]),

    // Other Providers
    provideAnimations(),
    provideHttpClient(withInterceptors([firebaseAuthInterceptor]), withFetch()),
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    provideFirestore(() => getFirestore()),
    provideAuth(() => getAuth()),
    importProvidersFrom(CustomerLookupModule),
    {
      provide: CUSTOMER_LOOKUP_STRATEGY,
      useClass: MockCustomerLookupStrategy,
    },
    {
      provide: API_URL_BUILDER,
      useExisting: ApiUrlBuilderService,
    },
    {
      provide: OU_TREE_DATA_PROVIDER,
      useExisting: OrgUnitsDataService,
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { floatLabel: 'always' },
    },
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(appRoutes),
    provideAppInitializer(initializeAuthFactory),
  ],
};
